<template>
  <div>
    <div class="mb-5 flex items-center gap-3">
      <span class="text-slate-500"> Upload Assets/Files to this list </span>
      <DButton
        label="Browse Files"
        icon="pi pi-upload"
        @click="triggerFileInput"
        :loading="uploading"
        :disabled="uploading" />
      <input ref="fileInput" type="file" multiple class="hidden" @change="handleFileSelect" />
    </div>

    <div v-if="openSubFolderName" class="flex w-full items-center">
      <DButton @click="loadProject" variant="outlined" text>{{ openFolderName }}</DButton>
      - {{ openSubFolderName }}
    </div>
    <div class="mb-3 flex items-center gap-2">
      <DSelect
        v-model="selectedTypeFilter"
        :options="typeFilterOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Filter by type" />
    </div>
    <div class="mt-1 mb-4 flex w-full">
      <DDataTable :value="filterAssets" class="w-full" paginator :rows="5">
        <Column field="data.latest_version.thumbnail" header="Thumbnail" style="width: 80px">
          <template #body="slotProps">
            <div class="font-medium text-white">
              <img
                v-if="slotProps.data.latest_version.thumbnail"
                :src="slotProps.data.latest_version.thumbnail"
                class="mr-2 inline-block h-20 w-20 rounded object-cover"
                @error="$event.target.style.display = 'none'" />
              <span v-else class="text-xs text-gray-400">No Thumbnail</span>
            </div>
          </template>
        </Column>
        <Column field="data.latest_version.name" header="Name">
          <template #body="slotProps">
            <div
              v-if="slotProps.data.asset_type == 'folder'"
              @click="openFolder(slotProps.data.code, slotProps.data.name)">
              {{ slotProps.data.name }}
            </div>

            <div v-else>
              {{ slotProps.data.latest_version.name }}
            </div></template
          >
        </Column>
        <Column field="asset_type" header="Type" style="width: 80px">
          <template #body="slotProps">
            <span class="text-sm">
              {{
                slotProps.data.asset_type === 'VI'
                  ? 'Video'
                  : slotProps.data.asset_type === 'IM'
                    ? 'Image'
                    : slotProps.data.asset_type === 'TD'
                      ? '3D Model'
                      : slotProps.data.asset_type
              }}
            </span>
          </template>
        </Column>
        <Column field="data.updated_at" header="Updated">
          <template #body="slotProps">{{ slotProps.data.updated_at }}</template>
        </Column>
        <Column style="width: 5rem">
          <template #body="slotProps">
            <div class="flex items-center gap-1">
              <DButton type="button" icon="pi pi-plus" text rounded @click="selectProduct(slotProps.data)"></DButton>
              <DButton
                type="button"
                icon="pi pi-trash"
                text
                rounded
                severity="danger"
                @click="deleteAsset(slotProps.data)"></DButton>
            </div>
          </template>
        </Column>
      </DDataTable>
    </div>
  </div>
  <div class="flex items-center justify-end">
    <DButton
      type="button"
      label="Cancel"
      icon="pi pi-times"
      @click="closeDialog({ buttonType: 'Cancel' })"
      autofocus></DButton>
  </div>
</template>

<script setup>
import { useAssetStore } from '@/stores/api/asset';
import { getAssetTypeFromFile } from '@/utils/getAssetTypeFromFile';
import { DButton, DDataTable, DSelect, useConfirm, useToast } from '@disguise/cloud-ui';
import Column from 'primevue/column';
import { computed, inject, onMounted, ref } from 'vue';

const assetStore = useAssetStore();
const openFolderName = ref();
const openSubFolderName = ref();
const params = ref({});
const selectedTypeFilter = ref('');

const dialogRef = inject('dialogRef');

const toast = useToast();
const fileInput = ref(null);
const uploading = ref(false);

const { confirmDialog } = useConfirm();

onMounted(() => {
  params.value = dialogRef.value.data;
  loadProject();
});

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = async (event) => {
  const files = Array.from(event.target.files);
  if (!files || files.length === 0) return;

  const allowedTypes = params.value.filter === 'scene' ? new Set(['scene']) : new Set(['image', 'video']);
  const validFiles = [];
  const invalidFiles = [];

  for (let file of files) {
    const mimeType = getAssetTypeFromFile(file);
    if (allowedTypes.has(mimeType)) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    toast.add({
      severity: 'warn',
      summary: 'Some files were not uploaded',
      detail: `The following files have unsupported types and were skipped: ${invalidFiles.join(', ')}`,
      life: 7000,
      group: 'br',
    });
  }

  if (validFiles.length === 0) return;

  uploading.value = true;
  const fileCount = validFiles.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';

  toast.add({
    severity: 'info',
    summary: 'Upload Started',
    detail: `Uploading ${fileCount} ${fileWord}...`,
    life: 3000,
    group: 'br',
  });

  let successCount = 0;
  let errorCount = 0;
  try {
    for (const file of validFiles) {
      try {
        const asset = await assetStore.uploadFile(file);

        if (asset) {
          successCount++;
        }
      } catch (fileError) {
        console.error(`Error uploading file ${file.name}:`, fileError);
        errorCount++;

        toast.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: `Failed to upload ${file.name}`,
          life: 5000,
          group: 'br',
        });
      }
    }

    if (successCount > 0) {
      toast.add({
        severity: 'success',
        summary: 'Upload Complete',
        detail: `Successfully uploaded ${successCount} ${successCount === 1 ? 'file' : 'files'}`,
        life: 4000,
        group: 'br',
      });

      await loadProject();
    }

    if (errorCount > 0 && successCount === 0) {
      toast.add({
        severity: 'error',
        summary: 'All Uploads Failed',
        detail: `Failed to upload ${errorCount} ${errorCount === 1 ? 'file' : 'files'}`,
        life: 5000,
        group: 'br',
      });
    }
  } finally {
    uploading.value = false;

    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const deleteAsset = (asset) => {
  if (!asset) return;

  confirmDialog('Confirmation', `Permanently delete "${asset.name}" from your library?`, 'Delete', 'Cancel')
    .then(async (response) => {
      if (response) {
        try {
          await assetStore.destroy(asset.code);

          toast.add({
            severity: 'success',
            summary: 'Asset Deleted',
            detail: `${asset.name} has been removed from library`,
            life: 3000,
            group: 'br',
          });

          await loadProject();
        } catch (error) {
          console.error('Error deleting asset:', error);
          toast.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: `Failed to delete ${asset.name}`,
            life: 5000,
            group: 'br',
          });
        }
      }
    })
    .catch((err) => {});
};

const loadProject = () => {
  assetStore.fetchForFolder(assetStore.currentAsset.folder);
};

const closeDialog = (e) => {
  dialogRef.value.close(e);
};

const selectProduct = (data) => {
  dialogRef.value.close(data);
};

const openFolder = (folderId, folderName) => {
  assetStore.loadAssetChildren(folderId).then(() => {
    openSubFolderName.value = folderName;
  });
};

const typeFilterOptions = computed(() => {
  if (params.value.filter === 'scene') {
    return [
      { label: 'All Types', value: '' },
      { label: '3D Model', value: 'TD' },
    ];
  }
  return [
    { label: 'All Types', value: '' },
    { label: 'Video', value: 'VI' },
    { label: 'Image', value: 'IM' },
  ];
});

const baseAssetTypes = computed(() => {
  if (params.value.filter === 'scene') return ['TD'];
  return ['VI', 'IM'];
});

const filterAssets = computed(() => {
  let filtered = assetStore.assets.filter((asset) => baseAssetTypes.value.includes(asset.asset_type));

  if (selectedTypeFilter.value) {
    filtered = filtered.filter((asset) => asset.asset_type === selectedTypeFilter.value);
  }

  return filtered;
});
</script>
